import { apiFetch } from './client.js'

function normalizeAbTest(item) {
  if (!item || typeof item !== 'object') return null
  return {
    abTestId: item.ab_test_id || item.abTestId || null,
    abTestName: item.ab_test_name || item.abTestName || '',
    abVariantId: item.ab_variant_id || item.abVariantId || null,
    abVariantName: item.ab_variant_name || item.abVariantName || '',
  }
}

function normalizeUserParam(item) {
  if (!item || typeof item !== 'object') return null
  return {
    paramType: item.param_type || item.paramType || '',
    paramValue: item.param_value || item.paramValue || '',
  }
}

function normalizeConfigTester(item) {
  if (!item || typeof item !== 'object') return null
  return {
    id: item.id,
    applicationId: item.application_id || item.applicationId || null,
    remoteConfigId: item.remote_config_id || item.remoteConfigId || null,
    remoteConfigName: item.remote_config_name || item.remoteConfigName || '',
    segmentationId: item.segmentation_id || item.segmentationId || null,
    segmentationName: item.segmentation_name || item.segmentationName || '',
    qaId: item.qa_id || item.qaId || '',
    expire: item.expire ?? 25920000,
    description: item.description || '',
    isActive: Boolean(item.is_active ?? item.isActive),
    startTime: item.start_time || item.startTime || null,
    endTime: item.end_time || item.endTime || null,
    createdAt: item.created_at || item.createdAt || null,
    abTests: Array.isArray(item.ab_tests || item.abTests)
      ? (item.ab_tests || item.abTests).map(normalizeAbTest).filter(Boolean)
      : [],
    userParams: Array.isArray(item.user_params || item.userParams)
      ? (item.user_params || item.userParams).map(normalizeUserParam).filter(Boolean)
      : [],
  }
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function extractItem(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.item) return payload.item
    if (payload.data && !Array.isArray(payload.data)) return payload.data
  }
  return payload
}

export async function listConfigTesters(applicationId, status = '') {
  let path = `/apps/${encodeURIComponent(applicationId)}/config-testers`
  if (String(status || '').trim()) {
    path += `?status=${encodeURIComponent(String(status).trim())}`
  }
  const payload = await apiFetch(path)
  return extractList(payload).map(normalizeConfigTester).filter(Boolean)
}

export async function getConfigTester(applicationId, configTesterId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/config-testers/${encodeURIComponent(configTesterId)}`,
  )
  return normalizeConfigTester(extractItem(payload))
}

export async function createConfigTester(applicationId, values) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/config-testers`, {
    method: 'POST',
    body: {
      qa_id: String(values?.qaId || '').trim(),
      remote_config_id: values?.remoteConfigId || null,
      segmentation_id: values?.segmentationId || null,
      description: String(values?.description || '').trim(),
      expire: values?.expire == null || values?.expire === '' ? 25920000 : Number(values.expire),
      ab_tests: Array.isArray(values?.abTests)
        ? values.abTests
            .filter((t) => t.abTestId && t.abVariantId)
            .map((t) => ({ ab_test_id: t.abTestId, ab_variant_id: t.abVariantId }))
        : [],
      user_params: Array.isArray(values?.userParams)
        ? values.userParams
            .filter((p) => p.paramType && p.paramValue)
            .map((p) => ({ param_type: p.paramType, param_value: p.paramValue }))
        : [],
    },
  })
  return normalizeConfigTester(extractItem(payload))
}

export async function updateConfigTester(applicationId, configTesterId, values) {
  const body = {}
  if (values?.remoteConfigId !== undefined) body.remote_config_id = values.remoteConfigId
  if (values?.segmentationId !== undefined) body.segmentation_id = values.segmentationId
  if (values?.description !== undefined) body.description = String(values.description || '').trim()
  if (values?.expire !== undefined) body.expire = Number(values.expire)
  if (values?.abTests !== undefined) {
    body.ab_tests = values.abTests
      .filter((t) => t.abTestId && t.abVariantId)
      .map((t) => ({ ab_test_id: t.abTestId, ab_variant_id: t.abVariantId }))
  }
  if (values?.userParams !== undefined) {
    body.user_params = values.userParams
      .filter((p) => p.paramType && p.paramValue)
      .map((p) => ({ param_type: p.paramType, param_value: p.paramValue }))
  }

  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/config-testers/${encodeURIComponent(configTesterId)}`,
    { method: 'PATCH', body },
  )
  return normalizeConfigTester(extractItem(payload))
}

export async function stopConfigTester(applicationId, configTesterId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/config-testers/${encodeURIComponent(configTesterId)}/stop`,
    { method: 'POST' },
  )
  return normalizeConfigTester(extractItem(payload))
}
